import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as ROA from "fp-ts/ReadonlyArray"
import { actions, assign, raise } from "xstate"
import { assertEvent, maybeEvent } from "../../../../../_common/xstate-helpers"
import {
    assignFareCard,
    notifyInvalidParameter,
    raiseFareCardChanged,
    sendAllFaresEvents,
} from "../e-commerce-machine-helpers"
import { ECommerceEvent, ECommerceMachineOptions } from "../e-commerce-machine"
import { ECommerceContext } from "../e-commerce-machine-context"
import { FareCard, toFareCards } from "../../../read-models/fare-card"
import { fareCardByKey, maybeFareCardByIndex, maybeFareCardByKey } from "../../../read-models/fare"

type Context = ECommerceContext
type Events = ECommerceEvent

export const fareCardActions: ECommerceMachineOptions["actions"] = {
    setCruiseInfo: assign<Context, Events>({
        cruiseInfo: (_, ev) => {
            assertEvent(ev, "FARE_OPTIONS_LOADED")
            return { expiration: ev.response.cruiseExpiration }
        },
    }),
    setFareCards: actions.pure<Context, Events>((ctx, ev) => {
        assertEvent(ev, "FARE_OPTIONS_LOADED")
        const fareCards = toFareCards(ctx.sanityContent.faresContent, ev.response)
        return raiseFareCardsLoaded(fareCards)
    }),
    updateFareCardAsGood: actions.pure<Context, Events>((ctx, ev) =>
        F.pipe(
            O.Do,
            O.apS("ev", maybeEvent(ev, "FARE_PRICE_LOADED")),
            O.bind("fareCard", ({ ev }) => maybeFareCardByIndex(ctx, ev.index)),
            O.map(({ ev, fareCard }) => FareCard.asGood(ev.response, fareCard)),
            O.foldW(F.constUndefined, (newFareCard) => [
                assignFareCard(newFareCard, ctx),
                ...sendAllFaresEvents(ctx.fareCards, newFareCard),
            ]),
        ),
    ),
    updateFareCardAsError: actions.pure<Context, Events>((ctx, ev) =>
        F.pipe(
            O.Do,
            O.apS("ev", maybeEvent(ev, "FARE_PRICE_ERROR", "FARE_SUITE_PRICES_ERROR")),
            O.bind("fareCard", ({ ev }) => maybeFareCardByIndex(ctx, ev.index)),
            O.map(({ ev, fareCard }) => FareCard.asError(ev.code, fareCard)),
            O.foldW(F.constUndefined, (newFareCard) => [
                assignFareCard(newFareCard, ctx),
                ...sendAllFaresEvents(ctx.fareCards, newFareCard),
            ]),
        ),
    ),
    updateFareCardWithSuitePrices: actions.pure((ctx, ev) =>
        F.pipe(
            O.Do,
            O.apS("ev", maybeEvent(ev, "FARE_SUITE_PRICES_LOADED")),
            O.bind("fareCard", ({ ev }) => maybeFareCardByIndex(ctx, ev.index)),
            O.map(({ ev, fareCard }) => {
                const fareCardWithSuitePrices = FareCard.updateStatusWithSuitePrices(ev.response, fareCard)
                return ctx.selectedSuiteCategory
                    ? FareCard.updatePriceBySuiteCategory(ctx.selectedSuiteCategory, fareCardWithSuitePrices)
                    : fareCardWithSuitePrices
            }),
            O.foldW(F.constUndefined, (newFareCard) => [
                assignFareCard(newFareCard, ctx),
                ...sendAllFaresEvents(ctx.fareCards, newFareCard),
            ]),
        ),
    ),
    setAvailableFareCardsForSelectedSuiteCategory: actions.pure<Context, Events>((ctx) => {
        if (!ctx.selectedSuiteCategory) return []
        return [
            assign<Context, Events>({
                fareCards: availableFareCardsBySuiteCategory(ctx.fareCards, ctx.selectedSuiteCategory),
            }),
        ]
    }),
    updateFareCardAirClass: actions.pure<Context, Events>((ctx, ev) =>
        F.pipe(
            O.bindTo("ev")(maybeEvent(ev, "CHANGE_AIR_CLASS")),
            O.bind("fareCard", ({ ev }) => maybeFareCardByKey(ctx, ev.key)),
            O.chain(({ ev, fareCard }) => FareCard.updateAirClass(ev.airClass)(fareCard)),
            O.foldW(F.constUndefined, (newFareCard) => [
                assignFareCard(newFareCard, ctx),
                raiseFareCardChanged(newFareCard),
            ]),
        ),
    ),
    updateFareCardGateway: actions.pure<Context, Events>((ctx, ev) =>
        F.pipe(
            O.bindTo("ev")(maybeEvent(ev, "CHANGE_GATEWAY")),
            O.bind("fareCard", ({ ev }) => maybeFareCardByKey(ctx, ev.key)),
            O.chain(({ ev, fareCard }) => FareCard.updateGateway(ev.gatewayCode)(fareCard)),
            O.foldW(F.constUndefined, (newFareCard) => [
                assignFareCard(newFareCard, ctx),
                raiseFareCardChanged(newFareCard),
            ]),
        ),
    ),
    setSelectedFare: actions.pure<Context, Events>((ctx, ev) => {
        assertEvent(ev, "SELECT_FARE")
        const selectedFare = fareCardByKey(ctx, ev.key)
        if (!selectedFare) return [notifyInvalidParameter("selectedFare", ev.key)]
        return raiseFareSelected(selectedFare, ev.isAutomaticSelection)
    }),
    unselectFare: actions.pure<Context, Events>((ctx, ev) => {
        assertEvent(ev, "UNSELECT_FARE")
        return [
            assign<Context, Events>({ selectedFare: undefined, isAutomaticSelection: false }),
            raise<Context, Events, Events>({ type: "FARE_UNSELECTED" }),
        ]
    }),
    clearSelectedFare: assign<Context, Events>({ selectedFare: undefined }),
    checkAllFaresInError: actions.pure<Context, Events>((ctx, ev) =>
        F.pipe(
            maybeEvent(ev, "ALL_FARES_IN_ERROR"),
            O.chain(O.fromPredicate((ev) => ev.error !== "noAvailablePrice")),
            O.foldW(F.constUndefined, (ev) => [
                raise<Context, Events, Events>({
                    type: "ERROR",
                    kind: ev.error === "transient" ? "generic" : "fatal",
                    origin: "ecommerce",
                    errorCode: ev.error,
                    info: {},
                    message: "All fares are in error",
                }),
            ]),
        ),
    ),
}

export const availableFareCardsBySuiteCategory = (fareCards: readonly FareCard[], selectedSuiteCategory: string) =>
    F.pipe(
        fareCards,
        ROA.map((fareCard) => FareCard.updatePriceBySuiteCategory(selectedSuiteCategory, fareCard)),
    )

export const raiseFareCardsLoaded = (fareCards: ReadonlyArray<FareCard>) => [
    assign<Context, Events>({ fareCards }),
    raise<Context, Events, Events>({ type: "FARE_CARDS_LOADED", fareCards }),
]

export const raiseFareSelected = (selectedFare: FareCard, isAutomaticSelection: boolean = false) => [
    assign<Context, Events>({ selectedFare, isAutomaticSelection }),
    raise<Context, Events, Events>({ type: "FARE_SELECTED", selectedFare }),
]
